import { useSnackbar } from '@/hooks/useSnackbar';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Ficha, ItemArrayPlataformas } from '@/types/ficha';
import {
  recuperarFichaAdicional,
  recuperarFichaPorSlug,
  recuperarPlataformaCinema,
} from '@/services/fichaService';
import { useHomeData } from '@/hooks/useHomeData';
import { favoritarFicha, jaViFicha } from '@/services/playlistsService';
import { PlataformaStreaming } from '@/types/plataformaStreaming';
import { recuperarPlataformasStreaming } from '@/services/carteira/minhaCarteiraService';
import { ArgumentosAvaliarFicha } from '@/services/types/resenha';
import { avaliarFicha } from '@/services/resenhaService';
import { registrarEvento } from '@/services/googleAnalytics';
import { useSession } from 'next-auth/react';
import { useAuthenticationModal } from '@/hooks/useAuthenticationModal';

function useFichaPaginaHook(slug: string) {
  const [ficha, setFicha] = useState<Ficha>();
  const [plataformas, setPlataformas] = useState<PlataformaStreaming[]>();
  const [assistido, setAssistido] = useState(false);
  const [loadingFicha, setLoadingFicha] = useState(false);
  const [loadingJaVi, setLoadingJaVi] = useState(false);
  const [loadingEnviarResenha, setLoadingEnviarResenha] = useState(false);
  const [loadingFavoritar, setLoadingFavoritar] = useState(false);
  const [loadingPlataformas, setLoadingPlataformas] = useState(false);
  const [mostrandoModalPlaylists, setMostrandoModalPlaylists] = useState(false);
  const [avaliacao, setAvaliacao] = useState(0);
  const [mostrandoModalPlataformas, setMostrandoModalPlataformas] =
    useState(false);
  const [plataformaSelecionada, setPlataformaSelecionada] = useState('');

  const { mostrarSnackbar } = useSnackbar();
  const { recuperarListasUsuario, favoritos } = useHomeData();

  const { status } = useSession();

  const { mostrarAuthenticationModalContext } = useAuthenticationModal();

  const jaFoiExecutadoRecuperacaoResenhaPendente = useRef(false);

  useEffect(() => {
    if (jaFoiExecutadoRecuperacaoResenhaPendente.current) return;

    if (status !== 'authenticated') return;
    if (!ficha || !ficha.tituloID) return;

    const resenhaPendente = localStorage.getItem(
      'salvarResenhaUsuarioDeslogado@Chippu',
    );

    if (!resenhaPendente) return;

    const dados = JSON.parse(resenhaPendente);

    jaFoiExecutadoRecuperacaoResenhaPendente.current = true;

    const salvarResenhaUsuario = async () => {
      try {
        await enviarResenha(dados);
        localStorage.removeItem('salvarResenhaUsuarioDeslogado@Chippu');
      } catch (error) {
        console.error('Erro ao enviar resenha recuperada:', error);
      }
    };

    salvarResenhaUsuario();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, ficha?.tituloID]);

  async function carregarFicha() {
    try {
      setLoadingFicha(true);
      const responseFicha = await recuperarFichaPorSlug(slug);
      setFicha(responseFicha);
      carregarAdicional(responseFicha.tituloID);
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setLoadingFicha(false);
    }
  }

  async function carregarAdicional(id: string) {
    try {
      if (status !== 'authenticated') return;
      setLoadingFicha(true);
      const responseFichaAdicional = await recuperarFichaAdicional(id);

      if (responseFichaAdicional.sucesso === false) {
        throw new Error(responseFichaAdicional.mensagem);
      }

      setAssistido(responseFichaAdicional.modelo.jaVi);
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setLoadingFicha(false);
    }
  }

  async function enviarResenha(props: Omit<ArgumentosAvaliarFicha, 'fichaID'>) {
    try {
      setLoadingEnviarResenha(true);
      const responseAvaliarFicha = await avaliarFicha({
        fichaID,
        ...props,
      });

      if (responseAvaliarFicha.sucesso === false) {
        throw new Error(responseAvaliarFicha.mensagem);
      }

      mostrarSnackbar({
        color: 'success',
        highContrast: true,
        mensagem: responseAvaliarFicha.mensagem,
        size: 1,
        variant: 'solid',
      });
      return responseAvaliarFicha;
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setLoadingEnviarResenha(false);
    }
  }

  async function favoritar() {
    if (!ficha) return;

    if (status !== 'authenticated') return mostrarAuthenticationModalContext();

    try {
      setLoadingFavoritar(true);
      const favoritarResponse = await favoritarFicha({
        fichaID: fichaID,
        adicionar: !favoritado,
      });

      if (favoritarResponse.sucesso === false) {
        throw new Error(favoritarResponse.mensagem);
      }

      await recuperarListasUsuario({});

      return favoritarResponse;
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setLoadingFavoritar(false);
    }
  }

  async function jaVi() {
    registrarEvento('clique_marcar_como_visto', {
      ficha: ficha?.titulo,
      fichaID: ficha?.tituloID,
    });

    if (status !== 'authenticated') return mostrarAuthenticationModalContext();

    try {
      setLoadingJaVi(true);
      const jaViResponse = await jaViFicha({ fichaID: fichaID });

      if (jaViResponse.sucesso === false) {
        throw new Error(jaViResponse.mensagem);
      }

      setAssistido(true);
      return jaViResponse;
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setLoadingJaVi(false);
    }
  }

  async function recuperarPlataformas(): Promise<void> {
    try {
      setLoadingPlataformas(true);
      const plataformasResponse = await recuperarPlataformasStreaming();
      const cinemaResponse = await recuperarPlataformaCinema();

      setPlataformas([...plataformasResponse, cinemaResponse]);
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setLoadingPlataformas(false);
    }
  }

  function abrirModalPlaylists() {
    if (status !== 'authenticated') return mostrarAuthenticationModalContext();

    setMostrandoModalPlaylists(true);
    registrarEvento('clique_botao_salvar', {
      ficha: ficha?.titulo,
      fichaID: ficha?.tituloID,
    });
  }

  function fecharModalPlaylists() {
    setMostrandoModalPlaylists(false);
  }

  /**
   * Adicionar verificação do usuário logado!!!
   *
   * Caso eu clique para logar, vou salvar a informação no localStorage
   * e fazer com que seja salvo a resenha do usuário!!!
   */
  async function enviarComentario() {
    setMostrandoModalPlataformas(false);

    // @ts-expect-error Falta tipos do next
    const comentario = document?.getElementById('campoComentario')?.value;

    if (status !== 'authenticated') {
      localStorage.setItem(
        'salvarResenhaUsuarioDeslogado@Chippu',
        JSON.stringify({
          avaliacao,
          comentario,
          plataformaStreamingID: plataformaSelecionada,
        }),
      );

      return mostrarAuthenticationModalContext();
    }

    registrarEvento('clique_enviar_resenha', {
      ficha: ficha?.titulo,
      fichaID: ficha?.tituloID,
      nota: avaliacao,
      comentario: comentario,
      plataformaStreamingID: plataformaSelecionada,
    });

    const response = await enviarResenha({
      avaliacao,
      comentario,
      plataformaStreamingID: plataformaSelecionada,
    });

    if (response?.sucesso === true) {
      // @ts-expect-error Falta tipos do next
      document.getElementById('campoComentario').value = '';
      setAvaliacao(0);
    }

    setPlataformaSelecionada('');
  }

  function irParaAvaliacao() {
    registrarEvento('clique_escrever_critica', {
      ficha: ficha?.titulo,
      fichaID: ficha?.tituloID,
    });

    const element = document.getElementById('campoComentario');
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
  }

  function registrarCliquePlataforma(plataforma: ItemArrayPlataformas) {
    registrarEvento('clique_plataforma', {
      ficha: ficha?.titulo,
      fichaID: ficha?.tituloID,
      plataforma: plataforma?.plataformaStreaming?.nome,
      plataformaID: plataforma?.plataformaStreamingID,
      url: plataforma?.link,
    });
  }

  const fichaID = useMemo(() => ficha?.tituloID || '', [ficha]);

  useEffect(() => {
    carregarFicha();
    // carregarAdicional();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fichaID]);

  const embededUrl = useMemo(() => {
    if (!ficha?.linkTrailer) {
      return '';
    }

    if (
      ficha?.linkTrailer.includes('youtube') &&
      ficha?.linkTrailer.includes('watch?v=')
    ) {
      return ficha?.linkTrailer.replace('watch?v=', 'embed/');
    }

    return ficha?.linkTrailer;
  }, [ficha]);

  const favoritado = useMemo(
    () => favoritos?.some((f) => f.tituloID === fichaID) || false,
    [favoritos, fichaID],
  );

  return {
    ficha,
    assistido,
    embededUrl,
    loadingFicha,
    loadingFavoritar,
    loadingEnviarResenha,
    enviarResenha,
    favoritar,
    mostrandoModalPlaylists,
    abrirModalPlaylists,
    fecharModalPlaylists,
    jaVi,
    loadingJaVi,
    favoritado,
    recuperarPlataformas,
    plataformas,
    loadingPlataformas,
    avaliacao,
    setAvaliacao,
    plataformaSelecionada,
    setPlataformaSelecionada,
    mostrandoModalPlataformas,
    setMostrandoModalPlataformas,
    enviarComentario,
    irParaAvaliacao,
    fichaID,
    registrarCliquePlataforma,
  };
}

export type useFichaPaginaHookType = ReturnType<typeof useFichaPaginaHook>;

export default useFichaPaginaHook;
